import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Button } from '@this/shared/ui/inputs/button';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { Fetcher } from '@this/src/util';
import { createTheme, type Themes } from '@this/constants/themes';
import type { AITTheme } from '@this/shared/ui/theme';
import { getBaseReceiptFormStyles } from './receipt_form_styles';

interface RouteParams {
  tripId: string;
}

const TripReceiptRevisionRequest: React.FC = () => {
  const { tripId } = useParams<RouteParams>();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [revisionReason, setRevisionReason] = useState('');
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubmit = async () => {
    setError(null);

    if (!revisionReason.trim()) {
      setError('内容は必須項目です。修正したい内容を記載してください。');
      return;
    }

    if (!isConfirmed) {
      setError('確認事項にチェックを入れてください。');
      return;
    }

    try {
      setIsLoading(true);

      const response = await Fetcher.post<{ success: boolean; error?: string }>(
        `/trip_receipts/${tripId}/revision_request`,
        {
          revision_reason: revisionReason
        }
      );

      if (response.success) {
        history.goBack();
      } else {
        setError(response.error || '訂正依頼の送信に失敗しました。');
      }
    } catch (error) {
      console.error('訂正依頼の送信に失敗しました', error);
      setError('訂正依頼の送信に失敗しました。');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    history.goBack();
  };
  const theme = createTheme(1);
  const styles = getStyles(isLoading, theme, isMobile);

  return (
    <div style={styles.container}>
      <div style={styles.title}>領収書訂正依頼</div>

      {isLoading && (
        <div style={styles.loadingOverlay}>
          <Loading loading />
        </div>
      )}

      <div style={styles.section}>
        <div style={styles.subTitle}>旅程番号</div>
        <div style={styles.childContent}>
          <div style={styles.tripIdDisplay} data-wovn-ignore>
            {tripId}
          </div>
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.subTitle}>依頼内容</div>
        <div style={styles.childContent}>
          <div style={styles.label}>
            内容 <span style={styles.required}>*</span>
          </div>
          <div style={styles.textareaContainer}>
            <textarea
              value={revisionReason}
              onChange={e => setRevisionReason(e.target.value)}
              placeholder="訂正したい内容を記載してください"
              style={styles.textarea}
            />
            {error && <div style={styles.errorText}>{error}</div>}
          </div>
        </div>
      </div>

      <div style={styles.section}>
        <div style={{ ...styles.childContent, flexDirection: 'column', gap: 0 }}>
          <div style={styles.noticeText}>[注意事項]</div>
          <ul style={styles.noticeList}>
            <li>訂正する内容（金額・旅程情報等）を具体的に記載してください。</li>
            <li>訂正の場合、元の領収書を破棄し、再発行いたします。</li>
            <li>
              訂正処理が完了し、領収書発行要件を満たすまで再発行はいたしかねますので、ご了承ください
              <br />
              （処理状況は、領収書発行メニュー上の「ステータス」をご確認ください）。
            </li>
          </ul>
        </div>
        <div style={styles.childContent}>
          <div style={styles.checkboxContainer}>
            <input
              type="checkbox"
              checked={isConfirmed}
              onChange={e => setIsConfirmed(e.target.checked)}
              id="confirmCheckbox"
            />
            <label htmlFor="confirmCheckbox" style={styles.checkText}>
              注意事項を確認しました。訂正を依頼します。
            </label>
          </div>
        </div>
      </div>

      <div style={styles.buttonContainer}>
        <a onClick={handleCancel} style={styles.confirmBack}>
          &lt; 戻る
        </a>
        <Button
          color="primary"
          size="medium"
          loading={isLoading}
          disabled={isLoading || !isConfirmed}
          onClick={handleSubmit}
        >
          訂正依頼
        </Button>
      </div>
    </div>
  );
};

function getStyles(isLoading: boolean, theme: Themes & AITTheme, isMobile: boolean) {
  const base = getBaseReceiptFormStyles(isLoading, theme);

  return {
    ...base,
    label: {
      ...base.label,
      alignItems: 'flex-start',
      width: isMobile ? '100%' : '200px'
    } as React.CSSProperties,

    childContent: {
      ...base.childContent,
      flexDirection: isMobile ? ('column' as const) : ('row' as const)
    } as React.CSSProperties,

    tripIdDisplay: {
      fontSize: '13px',
      wordWrap: 'break-word',
      padding: '8px 12px',
      borderRadius: '4px',
      display: 'inline-block'
    } as React.CSSProperties,

    textareaContainer: {
      flex: isMobile ? undefined : 1,
      width: isMobile ? '100%' : undefined
    } as React.CSSProperties,

    textarea: {
      width: '100%',
      minHeight: '120px',
      padding: '12px',
      border: '1px solid #ccc',
      borderRadius: '4px',
      fontSize: '14px',
      resize: 'vertical',
      fontFamily: 'inherit'
    } as React.CSSProperties,

    noticeList: {
      margin: '8px 0 0 0',
      paddingLeft: '24px',
      fontSize: '14px',
      lineHeight: 1.6,
      listStyle: 'disc'
    } as React.CSSProperties
  };
}

export default TripReceiptRevisionRequest;
